import type { EligibilityCheckInput, EligibilityCheckResult } from './ffs-api.js';

/**
 * When a card requires full-page 3-D Secure, Stripe's `confirmSetup` navigates the browser away
 * and returns to `return_url` — resetting all in-memory React state. We stash just enough of the
 * funnel here (sessionStorage, same-origin, this tab only) to resume on return: finalize the
 * Inbox Health SetupIntent and land the member back on the booking step. Cleared immediately on
 * consume. This is the member's own data on their own device, never sent anywhere new.
 */
const RESUME_KEY = 'ffs.payment.resume';

export interface PaymentResume {
  readonly inboxHealthIntentId: string;
  readonly patientId: string;
  readonly bookingUrl: string;
  readonly member: EligibilityCheckInput;
  readonly result: EligibilityCheckResult;
}

export function savePaymentResume(resume: PaymentResume): void {
  try {
    sessionStorage.setItem(RESUME_KEY, JSON.stringify(resume));
  } catch {
    // sessionStorage unavailable (private mode / disabled) — resume simply won't be possible.
  }
}

export function loadPaymentResume(): PaymentResume | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    return raw ? (JSON.parse(raw) as PaymentResume) : null;
  } catch {
    return null;
  }
}

export function clearPaymentResume(): void {
  try {
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    // ignore
  }
}

/**
 * Stripe appends `redirect_status` (+ `setup_intent`, `setup_intent_client_secret`) to the
 * return URL after a 3-D Secure redirect. `null` when this isn't a payment return.
 */
export function readStripeRedirectStatus(search: string = window.location.search): string | null {
  try {
    return new URLSearchParams(search).get('redirect_status');
  } catch {
    return null;
  }
}
